import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayArrow,
  Visibility,
  Refresh,
  CheckCircle as ApproveIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { FacilitySearchResult, CostReport, Report, ReportType } from '../types';
import NotifyCustomerModal from '../components/NotifyCustomerModal';
import ContactErnieModal from '../components/ContactErnieModal';
import ReportTypeChip from '../components/ReportTypeChip';

type TimePeriodMode = 'accident_date' | 'manual_years';

// the firm home buttons
const HOME_BUTTON = {
  textTransform: 'none', minWidth: 240, py: 1.25, fontSize: '1rem', fontWeight: 600,
  color: '#111827', bgcolor: '#FFFFFF', border: '2px solid #10A4C2',
  '&:hover': { border: '2px solid #0D8DA7', bgcolor: '#E8F8FB' },
} as const;

const US_STATES: { abbr: string; name: string }[] = [
  { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' }, { abbr: 'AZ', name: 'Arizona' },
  { abbr: 'AR', name: 'Arkansas' }, { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' }, { abbr: 'DC', name: 'District of Columbia' },
  { abbr: 'FL', name: 'Florida' }, { abbr: 'GA', name: 'Georgia' }, { abbr: 'HI', name: 'Hawaii' },
  { abbr: 'ID', name: 'Idaho' }, { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' },
  { abbr: 'IA', name: 'Iowa' }, { abbr: 'KS', name: 'Kansas' }, { abbr: 'KY', name: 'Kentucky' },
  { abbr: 'LA', name: 'Louisiana' }, { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' },
  { abbr: 'MA', name: 'Massachusetts' }, { abbr: 'MI', name: 'Michigan' }, { abbr: 'MN', name: 'Minnesota' },
  { abbr: 'MS', name: 'Mississippi' }, { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' },
  { abbr: 'NE', name: 'Nebraska' }, { abbr: 'NV', name: 'Nevada' }, { abbr: 'NH', name: 'New Hampshire' },
  { abbr: 'NJ', name: 'New Jersey' }, { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' },
  { abbr: 'NC', name: 'North Carolina' }, { abbr: 'ND', name: 'North Dakota' }, { abbr: 'OH', name: 'Ohio' },
  { abbr: 'OK', name: 'Oklahoma' }, { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' },
  { abbr: 'RI', name: 'Rhode Island' }, { abbr: 'SC', name: 'South Carolina' }, { abbr: 'SD', name: 'South Dakota' },
  { abbr: 'TN', name: 'Tennessee' }, { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' },
  { abbr: 'VT', name: 'Vermont' }, { abbr: 'VA', name: 'Virginia' }, { abbr: 'WA', name: 'Washington' },
  { abbr: 'WV', name: 'West Virginia' }, { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' },
];


export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.is_superuser ?? false;

  // New report panel — open by default


  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [searchResults, setSearchResults] = useState<FacilitySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<FacilitySearchResult | null>(null);

  // Time period state
  const [timePeriodMode, setTimePeriodMode] = useState<TimePeriodMode>('manual_years');
  const [accidentDate, setAccidentDate] = useState('');
  const [costReports, setCostReports] = useState<CostReport[]>([]);
  const [selectedReports, setSelectedReports] = useState<CostReport[]>([]);

  // Report state
  const [analyses, setAnalyses] = useState<Report[]>([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Firm home: request a report in their own words, or ask a question
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  // Admin: full report (generated) or mediation report (uploaded on the report page)
  const [newReportType, setNewReportType] = useState<ReportType>('full');
  // Superuser filters
  // ?account=<id> (from a client card's New report) preselects the client
  const [accountFilter, setAccountFilter] = useState(searchParams.get('account') || '');
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const filteredAccountName = accounts.find(a => a.id === accountFilter)?.name;
  const [statusFilter, setStatusFilter] = useState('');

  // Reports filter + pagination
  const [reportSearch, setReportSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reportPage, setReportPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(reportSearch), 300);
    return () => clearTimeout(t);
  }, [reportSearch]);

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAnalysis, setMenuAnalysis] = useState<Report | null>(null);

  // Notify customer modal
  const [notifyReport, setNotifyReport] = useState<Report | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch accounts for superuser filter
  useEffect(() => {
    if (!user?.is_superuser) return;
    api.get('/accounts/').then(res => setAccounts(res.data)).catch(() => {});
  }, [user]);

  // Debounced facility search
  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const params: Record<string, string> = { q: value };
        if (stateFilter) params.state = stateFilter;
        const res = await api.get('/facilities/search/', { params });
        setSearchResults(res.data);
      } catch {
        setSearchResults([]);
      }
      setSearchLoading(false);
    }, 300);
  }, [stateFilter]);

  // Load cost reports when facility selected
  useEffect(() => {
    if (!selectedFacility) {
      setCostReports([]);
      setSelectedReports([]);
      return;
    }
    api.get(`/facilities/${selectedFacility.ccn}/cost-reports/`)
      .then(res => {
        const reports = (res.data as CostReport[])
          .filter(r => r.fiscal_year >= 2020 && r.fiscal_year <= 2025)
          .sort((a, b) => a.fy_bgn_dt.localeCompare(b.fy_bgn_dt));
        setCostReports(reports);
        // Auto-select latest 3
        setSelectedReports(reports.slice(-3));
      })
      .catch(() => setCostReports([]));
  }, [selectedFacility]);

  // Auto-select 3 surrounding cost reports when accident date changes
  useEffect(() => {
    if (timePeriodMode !== 'accident_date' || !accidentDate || costReports.length === 0) return;

    // Find the cost report that contains the accident date
    const accDate = new Date(accidentDate);
    let containingIdx = -1;
    for (let i = 0; i < costReports.length; i++) {
      const bgn = new Date(costReports[i].fy_bgn_dt);
      const end = new Date(costReports[i].fy_end_dt);
      if (accDate >= bgn && accDate <= end) {
        containingIdx = i;
        break;
      }
    }

    // If no exact match, find the closest report after the accident date
    if (containingIdx === -1) {
      for (let i = 0; i < costReports.length; i++) {
        const bgn = new Date(costReports[i].fy_bgn_dt);
        if (bgn > accDate) {
          containingIdx = i;
          break;
        }
      }
    }
    // If still no match, use the last report
    if (containingIdx === -1) containingIdx = costReports.length - 1;

    // Select up to 3: one before, the containing one, one after
    // Anchor the end first so we always get 3 when possible
    const endIdx = Math.min(costReports.length, containingIdx + 2);
    const startIdx = Math.max(0, endIdx - 3);
    setSelectedReports(costReports.slice(startIdx, endIdx));
  }, [accidentDate, costReports, timePeriodMode]);

  // Load analyses
  const loadAnalyses = useCallback(() => {
    const params: Record<string, string> = { page: String(reportPage), page_size: '25' };
    if (user?.is_superuser && accountFilter) {
      params.account = accountFilter;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    api.get('/reports/', { params }).then(res => {
      setAnalyses(res.data.results);
      setTotalPages(res.data.total_pages);
      setTotalCount(res.data.count);
      setReportsLoaded(true);
    }).catch(() => { setReportsLoaded(true); });
  }, [user, accountFilter, statusFilter, debouncedSearch, reportPage]);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  // Poll running analyses
  useEffect(() => {
    const running = analyses.some(a => a.status === 'pending' || a.status === 'running');
    if (!running) return;
    const interval = setInterval(loadAnalyses, 3000);
    return () => clearInterval(interval);
  }, [analyses, loadAnalyses]);

  const handleRequestReport = () => {
    if (!selectedFacility) return;
    setShowConfirmModal(true);
  };

  const handleAdminGenerate = async () => {
    if (!selectedFacility) return;
    setGenerating(true);
    setError('');
    setShowConfirmModal(false);
    try {
      const payload: any = {
        ccn: selectedFacility.ccn,
        facility_name: selectedFacility.facility_name,
        state: selectedFacility.state,
      };
      if (selectedReports.length > 0) {
        payload.report_ids = selectedReports.map(r => r.rpt_rec_num);
      }
      if (timePeriodMode === 'accident_date' && accidentDate) {
        payload.accident_date = accidentDate;
      }
      if (accountFilter) {
        payload.account_id = accountFilter;
      }

      const { data } = await api.post('/reports/create/', payload);
      await api.post(`/reports/${data.uuid}/approve/`);
      loadAnalyses();

      setSelectedFacility(null);
      setAccidentDate('');
      setSelectedReports([]);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to generate report');
    }
    setGenerating(false);
  };

  const sendReportRequest = async () => {
    setRequestSaving(true);
    setRequestError('');
    try {
      await api.post('/account/report-requests/', { message: requestText.trim() });
      setRequestOpen(false);
      setRequestText('');
      setRequestMessage("Request sent. We'll be in touch shortly.");
    } catch (e: any) {
      setRequestError(e.response?.status === 429
        ? "You've sent a lot of messages in the last hour. Please try again later."
        : e.response?.data?.message?.[0] || e.response?.data?.error || 'Could not send your request.');
    }
    setRequestSaving(false);
  };

  // Mediation reports are not generated: create the row, then upload the
  // document on the report page.
  const handleCreateMediation = async () => {
    if (!selectedFacility) return;
    setGenerating(true);
    setError('');
    try {
      const payload: any = {
        ccn: selectedFacility.ccn,
        facility_name: selectedFacility.facility_name,
        state: selectedFacility.state,
        report_type: 'mediation',
      };
      if (accountFilter) payload.account_id = accountFilter;
      const { data } = await api.post('/reports/create/', payload);
      navigate(`/reports/${data.uuid}`);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to create report');
    }
    setGenerating(false);
  };

  const handleApprove = async (uuid: string) => {
    try {
      await api.post(`/reports/${uuid}/approve/`);
      loadAnalyses();
    } catch {
      // Silently handle
    }
  };


  const handleRename = async (uuid: string, newName: string) => {
    try {
      await api.patch(`/reports/${uuid}/rename/`, { display_name: newName });
      loadAnalyses();
    } catch {
      // Silently handle
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      await api.delete(`/reports/${uuid}/delete/`);
      loadAnalyses();
    } catch {
      // Silently handle
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, report: Report) => {
    setMenuAnchor(event.currentTarget);
    setMenuAnalysis(report);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAnalysis(null);
  };

  const canToggleReport = (report: CostReport, selected: CostReport[]): { allowed: boolean; reason?: string } => {
    if (isAdmin) return { allowed: true };

    const isSelected = selected.some(r => r.rpt_rec_num === report.rpt_rec_num);

    if (isSelected) {
      // Can only remove first or last
      if (selected.length <= 1) return { allowed: true };
      const sorted = [...selected].sort((a, b) => a.fy_bgn_dt.localeCompare(b.fy_bgn_dt));
      const isFirst = report.rpt_rec_num === sorted[0].rpt_rec_num;
      const isLast = report.rpt_rec_num === sorted[sorted.length - 1].rpt_rec_num;
      if (!isFirst && !isLast) return { allowed: false, reason: 'Can only remove the first or last report to keep selection consecutive' };
      return { allowed: true };
    }

    // Adding — must be adjacent to current selection
    if (selected.length === 0) return { allowed: true };
    if (selected.length >= 3) return { allowed: false, reason: 'Maximum 3 cost reports' };

    const allSorted = [...selected, report].sort((a, b) => a.fy_bgn_dt.localeCompare(b.fy_bgn_dt));
    const allIdx = costReports.map(r => r.rpt_rec_num);
    const selectedIdxs = allSorted.map(r => allIdx.indexOf(r.rpt_rec_num));
    for (let i = 1; i < selectedIdxs.length; i++) {
      if (selectedIdxs[i] - selectedIdxs[i - 1] !== 1) {
        return { allowed: false, reason: 'Cost reports must be consecutive' };
      }
    }
    return { allowed: true };
  };

  const toggleReport = (report: CostReport) => {
    setSelectedReports(prev => {
      const { allowed } = canToggleReport(report, prev);
      if (!allowed) return prev;
      const isSelected = prev.some(r => r.rpt_rec_num === report.rpt_rec_num);
      if (isSelected) return prev.filter(r => r.rpt_rec_num !== report.rpt_rec_num);
      if (prev.length >= 3) return prev;
      return [...prev, report].sort((a, b) => a.fy_bgn_dt.localeCompare(b.fy_bgn_dt));
    });
  };

  const formatReportLabel = (r: CostReport) => {
    const fmt = (d: string) => {
      const [y, m, day] = d.split('-');
      return `${parseInt(m)}/${parseInt(day)}/${y}`;
    };
    return `${fmt(r.fy_bgn_dt)} – ${fmt(r.fy_end_dt)}`;
  };



  const showAccountColumn = user?.is_superuser;

  return (
    <Box>
      {/* New Report generator — admins only; firms ask in their own words */}
      {isAdmin && <Paper
        sx={{
          p: 3, mb: 4, bgcolor: '#FFFFFF', border: '1px solid', borderColor: 'divider', borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#102B3D' }}>
            {filteredAccountName ? `New Report for ${filteredAccountName}` : 'New Report'}
          </Typography>
          <ToggleButtonGroup
            value={newReportType}
            exclusive
            onChange={(_, v) => v && setNewReportType(v)}
            size="small"
          >
            <ToggleButton value="full" sx={{ textTransform: 'none' }}>Financial & staffing analysis</ToggleButton>
            <ToggleButton value="mediation" sx={{ textTransform: 'none' }}>Mediation report</ToggleButton>
          </ToggleButtonGroup>
        </Box>
          <Box sx={{ display: 'flex' }}>
            {/* Left column: Search + facility info */}
            <Box sx={{ flex: 1, minWidth: 0, pr: selectedFacility ? 3 : 0, maxWidth: selectedFacility ? '50%' : '50%' }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(o) => o.name}
                  value={US_STATES.find(s => s.abbr === stateFilter) || null}
                  onChange={(_, val) => { setStateFilter(val?.abbr || ''); setSearchResults([]); }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.abbr}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>{option.abbr}</Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="State" placeholder="All states" size="small" />
                  )}
                  sx={{ width: 200 }}
                  size="small"
                />
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={searchResults}
                  getOptionLabel={(o) => `${o.facility_name} (${o.ccn}) - ${o.street_address ? `${o.street_address}, ` : ''}${o.city || ''}, ${o.state}`}
                  getOptionDisabled={(o) => !o.num_cost_reports}
                  loading={searchLoading}
                  onInputChange={(_, value) => handleSearchInput(value)}
                  onChange={(_, value) => setSelectedFacility(value)}
                  isOptionEqualToValue={(a, b) => a.ccn === b.ccn}
                  renderOption={(props, option) => (
                    <li {...props} key={option.ccn}>
                      <Box sx={{ opacity: option.num_cost_reports ? 1 : 0.5 }}>
                        <Typography variant="body2">
                          {option.facility_name}
                          {!option.num_cost_reports && (
                            <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                              No cost reports
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CCN: {option.ccn} | {option.street_address ? `${option.street_address}, ` : ''}{option.city}, {option.state}
                          {option.beds ? ` | ${option.beds} beds` : ''}
                          {option.num_cost_reports ? ` | ${option.num_cost_reports} cost reports` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search facility by name, CCN, city, or state"
                      placeholder="Start typing..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  )}
                />
              </Box>

              {selectedFacility && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#EAF1F5', borderRadius: 1.5, border: '1px solid #D7E1E8' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedFacility.facility_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    CCN: {selectedFacility.ccn} | {selectedFacility.street_address ? `${selectedFacility.street_address}, ` : ''}{selectedFacility.city}, {selectedFacility.state}
                    {selectedFacility.beds ? ` | ${selectedFacility.beds} beds` : ''}
                    {selectedFacility.num_cost_reports ? ` | ${selectedFacility.num_cost_reports} cost reports` : ''}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Vertical divider */}
            {selectedFacility && (
              <Box sx={{ width: '1px', bgcolor: 'divider', mx: 0, flexShrink: 0 }} />
            )}

            {/* Right column, mediation: no periods to pick; the document is uploaded on the report page */}
            {selectedFacility && newReportType === 'mediation' && (
              <Box sx={{ flex: 1, minWidth: 0, pl: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  A mediation report is not generated. Create it, then upload the PDF on the report page.
                  The client sees only its documents.
                </Typography>
                {!accountFilter && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Pick the client in the Accounts filter below first, so the report is filed under them.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreateMediation}
                  disabled={generating || !accountFilter}
                  sx={{ bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' }, py: 1.2 }}
                >
                  {generating ? 'Creating...' : 'Create Mediation Report'}
                </Button>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
              </Box>
            )}

            {/* Right column: Report selection + generate (only when facility selected) */}
            {selectedFacility && newReportType === 'full' && (
              <Box sx={{ flex: 1, minWidth: 0, pl: 3 }}>
                <ToggleButtonGroup
                  value={timePeriodMode}
                  exclusive
                  onChange={(_, v) => v && setTimePeriodMode(v)}
                  size="small"
                  sx={{ mb: 1.5 }}
                >
                  <ToggleButton value="manual_years">Select Reports</ToggleButton>
                  <ToggleButton value="accident_date">Accident Date</ToggleButton>
                </ToggleButtonGroup>

                {timePeriodMode === 'accident_date' ? (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      type="date"
                      label="Accident Date"
                      value={accidentDate}
                      onChange={(e) => setAccidentDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                      size="small"
                      fullWidth
                    />
                    {accidentDate && selectedReports.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Cost reports selected ({selectedReports.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {selectedReports.map(r => (
                            <Chip
                              key={r.rpt_rec_num}
                              label={formatReportLabel(r)}
                              size="small"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {accidentDate && selectedReports.length === 0 && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        No cost reports found near this date
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    {costReports.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No cost reports found (2020–2025)
                      </Typography>
                    ) : (
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                            Period
                          </Typography>
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 60, textAlign: 'center' }}>
                            FY
                          </Typography>
                          <Box sx={{ width: 40 }} />
                        </Box>
                        {costReports.map((report, idx) => {
                          const isSelected = selectedReports.some(r => r.rpt_rec_num === report.rpt_rec_num);
                          const { allowed, reason } = canToggleReport(report, selectedReports);
                          const isDisabled = !allowed;
                          const row = (
                            <Box
                              key={report.rpt_rec_num}
                              onClick={() => !isDisabled && toggleReport(report)}
                              sx={{
                                display: 'flex', alignItems: 'center', px: 2, py: 0.75,
                                cursor: isDisabled ? 'default' : 'pointer',
                                bgcolor: isSelected ? 'rgba(16, 164, 194, 0.06)' : 'white',
                                borderBottom: idx < costReports.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                transition: 'background-color 0.1s',
                                '&:hover': isDisabled ? {} : { bgcolor: isSelected ? 'rgba(16, 164, 194, 0.1)' : '#f8f9fa' },
                              }}
                            >
                              <Typography variant="body2" sx={{ flex: 1, fontWeight: isSelected ? 600 : 400, fontSize: '0.85rem', color: '#102B3D' }}>
                                {formatReportLabel(report)}
                              </Typography>
                              <Typography variant="body2" sx={{ width: 60, textAlign: 'center', fontSize: '0.85rem', color: '#102B3D' }}>
                                {report.fiscal_year}
                              </Typography>
                              <Box sx={{ width: 40, textAlign: 'center' }}>
                                <Box sx={{
                                  width: 18, height: 18, borderRadius: '4px', mx: 'auto',
                                  border: '2px solid', borderColor: isSelected ? '#10A4C2' : '#ccc',
                                  bgcolor: isSelected ? '#10A4C2' : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  transition: 'all 0.15s',
                                }}>
                                  {isSelected && (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                          return isDisabled && reason ? (
                            <Tooltip key={report.rpt_rec_num} title={reason} arrow placement="top">
                              <span>{row}</span>
                            </Tooltip>
                          ) : row;
                        })}
                      </Box>
                    )}
                    {selectedReports.length > 0 && selectedReports.length < 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {selectedReports.length} of 3 selected — select up to 3 consecutive cost reports
                      </Typography>
                    )}
                    {selectedReports.length >= 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        3 cost reports selected
                      </Typography>
                    )}
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                  onClick={handleRequestReport}
                  disabled={generating || selectedReports.length === 0 || (timePeriodMode === 'accident_date' && !accidentDate)}
                  sx={{ bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' }, py: 1.2 }}
                >
                  {generating ? 'Submitting...' : 'Generate Report'}
                </Button>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
              </Box>
            )}
          </Box>
      </Paper>}

      {!isAdmin && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button variant="outlined" onClick={() => setRequestOpen(true)} sx={HOME_BUTTON}>
            Request a new report
          </Button>
          <Button variant="outlined" onClick={() => setContactOpen(true)} sx={HOME_BUTTON}>
            Ask Ernie a question
          </Button>
        </Box>
      )}

      <ContactErnieModal open={contactOpen} onClose={() => setContactOpen(false)} />

      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a new report</DialogTitle>
        <DialogContent>
          {requestError && <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            label="What do you need?"
            placeholder="Facility name, location, accident date, anything else we should know."
            value={requestText}
            onChange={(e) => setRequestText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendReportRequest} disabled={requestSaving || !requestText.trim()}>
            {requestSaving ? 'Sending...' : 'Send request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Divider */}
      <Box sx={{ my: 4, borderTop: '1px solid', borderColor: 'divider' }} />

      {requestMessage && (
        <Alert severity="success" onClose={() => setRequestMessage('')} sx={{ mb: 3 }}>
          {requestMessage}
        </Alert>
      )}

      {/* Reports section */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#102B3D' }}>
              Reports
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                placeholder="Search reports..."
                size="small"
                value={reportSearch}
                onChange={(e) => { setReportSearch(e.target.value); setReportPage(1); }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.disabled', fontSize: 18 }} />,
                }}
                sx={{ width: 220, '& .MuiOutlinedInput-root': { bgcolor: '#f8f9fa' } }}
              />
              {isAdmin && (
                <>
                  <TextField
                    select
                    size="small"
                    value={accountFilter}
                    onChange={(e) => { setAccountFilter(e.target.value); setReportPage(1); }}
                    sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: '#f8f9fa' } }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">All Accounts</MenuItem>
                    {accounts.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setReportPage(1); }}
                    sx={{ width: 140, '& .MuiOutlinedInput-root': { bgcolor: '#f8f9fa' } }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="requested">Requested</MenuItem>
                    <MenuItem value="pending">Queued</MenuItem>
                    <MenuItem value="running">Generating</MenuItem>
                    <MenuItem value="completed">Complete</MenuItem>
                    <MenuItem value="failed">Error</MenuItem>
                  </TextField>
                </>
              )}
              <IconButton onClick={loadAnalyses} size="small" title="Refresh"><Refresh /></IconButton>
            </Box>
          </Box>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5 }}>Facility</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5, width: 90 }}>CCN</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5, width: 50 }}>State</TableCell>
                {showAccountColumn && (
                  <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5 }}>Account</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5, width: 110 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#102B3D', fontSize: '0.8rem', py: 1.5, width: 100 }}>Created</TableCell>
                <TableCell sx={{ width: 48, py: 1.5 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!reportsLoaded ? (
                  <TableRow>
                    <TableCell colSpan={showAccountColumn ? 8 : 7} sx={{ py: 4, textAlign: 'center' }}>
                      <CircularProgress size={28} sx={{ color: '#10A4C2' }} />
                    </TableCell>
                  </TableRow>
              ) : analyses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showAccountColumn ? 8 : 7} sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {!statusFilter && !reportSearch && !accountFilter
                          ? (isAdmin ? 'No reports yet — search for a facility above to get started.' : 'No reports yet.')
                          : 'No reports match the current filters'}
                      </Typography>
                    </TableCell>
                  </TableRow>
              ) : analyses.map((a) => (
                <TableRow
                  key={a.id}
                  hover={a.status === 'completed' || !!user?.is_superuser}
                  onClick={() => {
                    if (a.status === 'completed' || user?.is_superuser) {
                      navigate(`/reports/${a.uuid}`);
                    }
                  }}
                  sx={{
                    cursor: (a.status === 'completed' || user?.is_superuser) ? 'pointer' : 'default',
                    '&:hover': (a.status === 'completed' || user?.is_superuser) ? { bgcolor: 'rgba(16, 164, 194, 0.04)' } : {},
                    '& td': { borderColor: '#f0f0f0', py: 1.75 },
                  }}
                >
                  <TableCell>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: '#102B3D',
                        display: 'inline',
                        cursor: 'text',
                        '&:hover': { outline: '1px solid #D7E1E8', borderRadius: 0.5, px: 0.5, mx: -0.5 },
                        '&:focus': { outline: '2px solid #10A4C2', borderRadius: 0.5, px: 0.5, mx: -0.5, bgcolor: '#fff' },
                      }}
                      contentEditable
                      suppressContentEditableWarning
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => {
                        const newName = (e.target as HTMLElement).textContent?.trim() || '';
                        const currentName = a.display_name || a.facility_name || a.ccn;
                        if (newName && newName !== currentName) {
                          handleRename(a.uuid, newName);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).blur(); }
                        if (e.key === 'Escape') {
                          (e.target as HTMLElement).textContent = a.display_name || a.facility_name || a.ccn;
                          (e.target as HTMLElement).blur();
                        }
                      }}
                    >
                      {a.display_name || a.facility_name || a.ccn}
                    </Typography>
                    <Box component="span" sx={{ ml: 1, verticalAlign: 'middle' }}>
                      <ReportTypeChip type={a.report_type} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                      {a.ccn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{a.state}</Typography>
                  </TableCell>
                  {showAccountColumn && (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{a.account_name || '—'}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {a.period || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {a.status === 'failed' ? (
                      <Tooltip title="Failed to generate report" arrow>
                        <Chip label="Error" size="small" variant="outlined" color="error" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                      </Tooltip>
                    ) : a.status === 'running' || a.status === 'pending' ? (
                      <Chip
                        icon={<CircularProgress size={12} />}
                        label="Generating"
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    ) : a.status === 'requested' || a.status === 'invoiced' ? (
                      <Chip
                        label="Requested"
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #FFE0B2' }}
                      />
                    ) : (
                      <Chip
                        label="Complete"
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {new Date(a.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, a); }}
                    >
                      <MoreVertIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
              <Typography variant="caption" color="text.secondary">
                {totalCount} report{totalCount !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  disabled={reportPage <= 1}
                  onClick={() => setReportPage(p => p - 1)}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Prev
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {reportPage} / {totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={reportPage >= totalPages}
                  onClick={() => setReportPage(p => p + 1)}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

      {/* Row action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem
          onClick={() => {
            if (menuAnalysis) navigate(`/reports/${menuAnalysis.uuid}`);
            handleMenuClose();
          }}
          disabled={menuAnalysis?.status !== 'completed' && !user?.is_superuser}
        >
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View Report</ListItemText>
        </MenuItem>
        {user?.is_superuser && menuAnalysis?.report_type !== 'mediation' && (menuAnalysis?.status === 'requested' || menuAnalysis?.status === 'invoiced') && (
          <MenuItem onClick={() => {
            if (menuAnalysis) handleApprove(menuAnalysis.uuid);
            handleMenuClose();
          }}>
            <ListItemIcon><ApproveIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Approve & Generate</ListItemText>
          </MenuItem>
        )}
        {user?.is_superuser && menuAnalysis?.status === 'completed' && (
          <MenuItem onClick={() => {
            if (menuAnalysis) setNotifyReport(menuAnalysis);
            handleMenuClose();
          }}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Notify Customer</ListItemText>
          </MenuItem>
        )}
        {user?.is_superuser && (
          <MenuItem
            onClick={() => {
              if (menuAnalysis) handleDelete(menuAnalysis.uuid);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Request confirmation modal */}
      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#102B3D', pb: 0 }}>
          Generate Report
        </DialogTitle>
        <DialogContent>
          {selectedFacility && (
            <Box sx={{ mt: 1 }}>
              {/* Facility */}
              <Box sx={{ p: 2, bgcolor: '#EAF1F5', borderRadius: 1.5, border: '1px solid #D7E1E8', mb: 2.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>{selectedFacility.facility_name}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  CCN: {selectedFacility.ccn} | {selectedFacility.street_address ? `${selectedFacility.street_address}, ` : ''}{selectedFacility.city}, {selectedFacility.state}
                  {selectedFacility.beds ? ` | ${selectedFacility.beds} beds` : ''}
                </Typography>
              </Box>

              {/* Cost reports */}
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                Cost Reports Included
              </Typography>
              <Box sx={{ mt: 0.5, mb: 2.5 }}>
                {selectedReports.map(r => (
                  <Typography key={r.rpt_rec_num} variant="body2" sx={{ color: '#102B3D', mb: 0.25 }}>
                    {formatReportLabel(r)} <span style={{ color: '#6B7280' }}>(FY {r.fiscal_year})</span>
                  </Typography>
                ))}
                {accidentDate && timePeriodMode === 'accident_date' && (
                  <Typography variant="body2" sx={{ color: '#102B3D', mt: 0.5 }}>
                    Accident date: {accidentDate}
                  </Typography>
                )}
              </Box>

              {/* Warnings */}
              {selectedReports.length === 2 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You have selected fewer than 3 cost reports. The latest cost report will be duplicated in the 3rd cost report column.
                </Alert>
              )}
              {selectedReports.length === 1 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You have selected only 1 cost report. This cost report will be duplicated across all 3 cost report columns.
                </Alert>
              )}
              {timePeriodMode === 'accident_date' && accidentDate && selectedReports.length > 0 &&
                new Date(accidentDate) > new Date(
                  [...selectedReports].sort((a, b) => b.fy_end_dt.localeCompare(a.fy_end_dt))[0].fy_end_dt
                ) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The accident date falls after the most recent available cost report period. This report will be generated using the most recent cost report data. When the 2025 cost report becomes available, we will provide an updated report at no additional cost.
                </Alert>
              )}

              <Divider sx={{ mb: 2.5 }} />

              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Report will be generated immediately.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleAdminGenerate}
                  disabled={generating}
                  sx={{ bgcolor: '#10A4C2', '&:hover': { bgcolor: '#0D8DA7' }, py: 1.3 }}
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={() => setShowConfirmModal(false)} sx={{ color: '#6B7280' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Notify Customer modal (from reports list) */}
      {notifyReport && (
        <NotifyCustomerModal
          open={!!notifyReport}
          onClose={() => setNotifyReport(null)}
          report={notifyReport}
          customerEmail={notifyReport.requested_by_email}
        />
      )}
    </Box>
  );
}
